/**
 * The first employee is the emotional peak of PLAYABLE_V1 (research finding:
 * the first hire is consistently described as the milestone moment in this
 * genre). Mechanically it is the moment the player stops being the worker.
 *
 * Design commitment C2 from the competitive analysis: delegation must cost
 * something ongoing. An employee is wages + a crew kit + slightly worse quality,
 * so hiring is a bet on having enough work to keep them busy - not a free
 * multiplier.
 *
 * Design commitment C3: management effort must be sub-linear. The player never
 * assigns individual jobs. Employees pull from the accepted lead pool by
 * themselves, all the player does is decide how many to employ.
 */
import type { GameState, Employee } from '../state/GameState.js';
import type { Rng } from '../core/rng.js';
import type { EventBus } from '../core/events.js';
import type { IdGenerator } from '../core/ids.js';
import type { FinanceSystem } from './FinanceSystem.js';
import type { BusinessSystem } from './BusinessSystem.js';
import type { CustomerSystem } from './CustomerSystem.js';
import type { ReputationSystem } from './ReputationSystem.js';
import { BALANCE } from '../data/balance.js';
import { FIRST_NAMES, LAST_NAMES } from '../data/catalog.js';

export interface Applicant {
  id: string;
  name: string;
  hourlyWage: number;
  skill: number;
}

export class EmployeeSystem {
  constructor(
    private readonly bus: EventBus,
    private readonly rng: Rng,
    private readonly ids: IdGenerator,
  ) {}

  /** Three candidates, so hiring is a choice between cheap and capable. */
  generateApplicants(state: GameState, count = 3): Applicant[] {
    const cfg = BALANCE.employees;
    const out: Applicant[] = [];
    for (let i = 0; i < count; i++) {
      const skill = this.rng.float(cfg.startingSkillMin, cfg.startingSkillMax);
      // Wage tracks skill, with enough noise that a bargain is sometimes there.
      const t = (skill - cfg.startingSkillMin) / (cfg.startingSkillMax - cfg.startingSkillMin);
      const wage = Math.round(
        cfg.minWage + (cfg.maxWage - cfg.minWage) * t * this.rng.float(0.85, 1.12),
      );
      out.push({
        id: this.ids.next('applicant'),
        name: `${this.rng.pick(FIRST_NAMES)} ${this.rng.pick(LAST_NAMES)}`,
        hourlyWage: Math.max(cfg.minWage, Math.min(cfg.maxWage, wage)),
        skill,
      });
    }
    return out;
  }

  canHire(state: GameState): { ok: boolean; reason?: string } {
    if (!state.business.registered) {
      return { ok: false, reason: 'You must register the business first.' };
    }
    if (state.business.employees.length >= BALANCE.employees.maxEmployees) {
      return { ok: false, reason: 'Your crew is at capacity.' };
    }
    if (state.business.crewKits <= state.business.employees.length) {
      return { ok: false, reason: 'You need another crew kit before hiring.' };
    }
    if (state.finance.cash < BALANCE.employees.hiringFee) {
      return { ok: false, reason: 'You cannot cover the hiring fee.' };
    }
    return { ok: true };
  }

  hire(state: GameState, applicant: Applicant, finance: FinanceSystem): boolean {
    const check = this.canHire(state);
    if (!check.ok) return false;
    if (!finance.debit(state, BALANCE.employees.hiringFee, 'fees', `Hire ${applicant.name}`)) {
      return false;
    }
    const employee: Employee = {
      id: this.ids.next('emp'),
      name: applicant.name,
      hourlyWage: applicant.hourlyWage,
      skill: applicant.skill,
      hiredOnDay: state.time.day,
      accruedWages: 0,
      jobsCompleted: 0,
      absentToday: false,
      currentLeadId: null,
      currentRemainingMinutes: 0,
      hasKit: true,
    };
    state.business.employees.push(employee);
    this.bus.emit({
      type: 'employee.hired',
      employeeId: employee.id,
      name: employee.name,
      wage: employee.hourlyWage,
    });
    return true;
  }

  fire(state: GameState, employeeId: string): boolean {
    const emp = state.business.employees.find((e) => e.id === employeeId);
    if (!emp) return false;
    state.business.employees = state.business.employees.filter((e) => e.id !== employeeId);
    // Release any lead they were holding so the player can still do it.
    for (const lead of state.leads) {
      if (lead.assignedEmployeeId === employeeId) lead.assignedEmployeeId = null;
    }
    this.bus.emit({ type: 'employee.fired', employeeId, name: emp.name });
    return true;
  }

  /** Employees work a fixed number of days per week. */
  private worksToday(state: GameState): boolean {
    return state.time.dayOfWeek < BALANCE.employees.daysPerWeek;
  }

  startDay(state: GameState): void {
    for (const emp of state.business.employees) {
      emp.absentToday = this.rng.chance(BALANCE.employees.absenceChance);
      emp.currentLeadId = null;
      emp.currentRemainingMinutes = 0;
    }
  }

  /**
   * Run every employee's whole working day at once.
   *
   * Resolving a day in a block rather than minute by minute keeps the
   * simulation cheap enough for the balance harness to run hundreds of games,
   * and keeps the player's experience honest: you leave in the morning, and in
   * the evening you find out what your crew got through.
   */
  runWorkingDay(
    state: GameState,
    business: BusinessSystem,
    customers: CustomerSystem,
    finance: FinanceSystem,
    reputation: ReputationSystem,
  ): { jobsDone: number; revenue: number; wages: number } {
    let jobsDone = 0;
    let revenue = 0;
    let wages = 0;
    if (!this.worksToday(state)) return { jobsDone, revenue, wages };

    const cfg = BALANCE.employees;
    const kitSpeed = business.speedMultiplier(state);
    const kitQuality = business.qualityBase(state);

    for (const emp of state.business.employees) {
      if (emp.absentToday) continue;

      // Wages are owed for the whole day, whether or not there is work. That is
      // the ongoing cost that makes hiring a bet rather than free money.
      const dayWage = emp.hourlyWage * cfg.hoursPerDay;
      emp.accruedWages += dayWage;
      wages += dayWage;

      let minutesLeft = cfg.hoursPerDay * 60;

      while (minutesLeft > 0) {
        const lead = this.pickLeadFor(state, emp);
        if (!lead) break;

        const base = BALANCE.yard.minutesBySize[lead.lotSize];
        const speed = kitSpeed * cfg.speedFactor * (0.7 + 0.6 * emp.skill);
        const minutes = Math.max(8, Math.round(base / Math.max(0.3, speed)));
        // Add travel between properties so a crew cannot service the whole
        // neighbourhood in a day.
        const withTravel = minutes + 12;
        if (withTravel > minutesLeft) break;

        minutesLeft -= withTravel;
        lead.assignedEmployeeId = emp.id;

        // Same quality curve as the owner, then scaled by the employee's skill
        // and a flat handicap. A green hire does noticeably worse work than the
        // player does with the identical kit - that is the cost of delegating.
        const skillFactor = 0.78 + 0.22 * emp.skill;
        const quality = clamp01(
          business.qualityCurve(kitQuality, 1, this.rng.float(-0.05, 0.05)) *
            cfg.qualityFactor *
            skillFactor,
        );
        const pay = Math.round(lead.payout * (0.85 + 0.15 * quality));
        finance.credit(state, pay, 'job', `${emp.name}: ${lead.customerName}`);
        state.business.lifetimeRevenue += pay;
        revenue += pay;

        reputation.applyJobResult(state, quality);
        customers.completeLead(state, lead, quality);

        emp.jobsCompleted += 1;
        emp.skill = Math.min(cfg.maxSkill, emp.skill + cfg.skillGrowthPerJob);
        state.statistics.employeeJobsCompleted += 1;
        jobsDone += 1;

        this.bus.emit({
          type: 'employee.completedJob',
          employeeId: emp.id,
          name: emp.name,
          pay,
        });
      }
    }

    return { jobsDone, revenue, wages };
  }

  /**
   * Employees take the work the player has said yes to. Contracted visits come
   * first because missing those is expensive, then the best-paying accepted lead.
   */
  private pickLeadFor(state: GameState, emp: Employee) {
    const candidates = state.leads.filter(
      (l) => l.accepted && l.assignedEmployeeId === null,
    );
    if (candidates.length === 0) return undefined;
    const contracted = candidates.filter((l) => l.fromContract);
    const pool = contracted.length > 0 ? contracted : candidates;
    return pool.reduce((best, l) => (l.payout > best.payout ? l : best), pool[0]!);
  }

  /** Weekly payroll. Unpaid wages become debt rather than being silently skipped. */
  payWages(state: GameState, finance: FinanceSystem): number {
    let total = 0;
    for (const emp of state.business.employees) {
      if (emp.accruedWages <= 0) continue;
      const amount = emp.accruedWages;
      finance.charge(state, amount, 'wages', `Wages - ${emp.name}`);
      state.business.lifetimeWages += amount;
      emp.accruedWages = 0;
      total += amount;
      this.bus.emit({ type: 'employee.paid', employeeId: emp.id, name: emp.name, amount });
    }
    return total;
  }

  /** Total weekly wage commitment, shown to the player before they hire. */
  weeklyWageBill(state: GameState): number {
    const cfg = BALANCE.employees;
    return state.business.employees.reduce(
      (sum, e) => sum + e.hourlyWage * cfg.hoursPerDay * cfg.daysPerWeek,
      0,
    );
  }
}

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}
